import { createContext } from 'react';
import {
  User,
  UserPreferences,
  ViewSelectProp,
} from '../../models/User';
import utils from 'utils';
import { Listing } from 'models/Listing';
import { DropzoneDelete, DropzoneUpload } from 'models/Dropzone';
import { Enterprise, EnterpriseMember } from 'models/Enterprises';
import { UpdatePasswordValues } from 'router/subrouters/Dash/subrouters/private/subrouters/Profile/hooks/useProfileForm';

export type CallbackConfig = {
  skipToast?: boolean;
  skipErrorToast?: boolean;
  onSuccess?: () => void;
  /**
   * Return true to claim the error - the caller renders it itself and the
   * default toast is suppressed.
   */
  onError?: (error?: unknown) => boolean | void;
};

type CurrentUserContextProps = {
  currentUser: User;
  isUserVerified: boolean;
  isSuperAdmin: boolean;
  isUltraAdmin: boolean;
  hasEnterprise: boolean;
  isEnterpriseHead: boolean;
  isOfficeManager: boolean;
  // Enterprise member that is neither an enterprise head nor an office manager.
  isRegularEnterpriseMember: boolean;
  isRWCMember: boolean;
  enterpriseMember: EnterpriseMember;
  enterprise: Enterprise;
  hasEnterpriseLogo: boolean;
  refetchCurrentUser: () => Promise<void>;
  updateCurrentUser: (user: Partial<User>) => void;
  openVerificationModal: (
    shouldCheckPhone?: boolean,
    listingId?: string,
  ) => void;
  openPhoneVerificationModal: () => void;
  patchCurrentUser: (
    user: Partial<User>,
    config?: CallbackConfig,
  ) => Promise<void>;
  updatePreferences: (preferences: Partial<UserPreferences>) => void;
  markSessionRead: (sessionId: string) => void;
  markSessionUnread: (sessionId: string) => void;
  uploadProfileImage: DropzoneUpload;
  deleteProfileImage: DropzoneDelete;
  isListingEnquirableAndWatchableByMe: (listing: Listing) => [boolean, boolean];
  changePassword: (
    value: UpdatePasswordValues,
    config?: CallbackConfig,
  ) => Promise<void>;
  updateView: (key: ViewSelectProp, value: boolean) => void;
};

export default createContext<Partial<CurrentUserContextProps>>({
  currentUser: null,
  isUserVerified: false,
  isSuperAdmin: false,
  isUltraAdmin: false,
  hasEnterprise: false,
  isEnterpriseHead: false,
  isOfficeManager: false,
  isRegularEnterpriseMember: false,
  isRWCMember: false,
  enterpriseMember: null,
  enterprise: null,
  hasEnterpriseLogo: false,
  refetchCurrentUser: utils.noop,
  updateCurrentUser: utils.noop,
  openVerificationModal: utils.noop,
  openPhoneVerificationModal: utils.noop,
  changePassword: utils.noop,
  patchCurrentUser: utils.noop,
  updatePreferences: utils.noop,
  uploadProfileImage: utils.noop,
  deleteProfileImage: utils.noop,
  markSessionRead: utils.noop,
  markSessionUnread: utils.noop,
  isListingEnquirableAndWatchableByMe: utils.noop,
  updateView: utils.noop,
});
